/**
 * TemplateModal
 *
 * Simplified modal for viewing a single graphics template.
 * Shows only the image, Edit in Canva button, and disclaimer.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../ui/notifications/toast-notification.jsx';
import { useTemplate } from '../../../api/content/templateActionsApi';
import './TemplateModal.css';

const TemplateModal = ({ 
  template, 
  isOpen, 
  onClose, 
  onNext, 
  onPrevious, 
  hasNavigation = false,
  isSaved = false,
  onSaveTemplate,
  isLoggedIn = false,
  userPlan = 'basic'
}) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const canvaButtonRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Tooltip state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, showAbove: false });
  const tooltipTriggerRef = useRef(null);

  // Auto-play video when modal opens (muted)
  useEffect(() => {
    if (isOpen && videoRef.current && template?.type?.toLowerCase() === 'video' && template?.videoUrl) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(error => {
        console.log('Auto-play prevented:', error);
      });
    }
  }, [isOpen, template]);

  // Pause video when modal closes
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasNavigation) {
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (hasNavigation) {
            onNext();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrevious, hasNavigation]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Add event listener for the Canva button hover effects
  useEffect(() => {
    const buttonElement = canvaButtonRef.current;
    
    if (buttonElement) {
      const handleMouseEnter = () => {
        buttonElement.style.background = "linear-gradient(45deg, #00D4DC, #8D3AF8)";
      };
      
      const handleMouseLeave = () => {
        buttonElement.style.background = "linear-gradient(45deg, #00C4CC, #7D2AE8)";
      };
      
      buttonElement.addEventListener('mouseenter', handleMouseEnter);
      buttonElement.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        buttonElement.removeEventListener('mouseenter', handleMouseEnter);
        buttonElement.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [isOpen]);

  // Handle save/unsave template
  const handleSaveClick = () => {
    if (onSaveTemplate) {
      onSaveTemplate(isSaved);
    }
  };

  // Handle Canva button click
  const handleCanvaClick = (e) => {
    // If user is on basic plan, redirect to pricing page
    if (userPlan === 'basic' || (user && user.planLevel === 'basic')) {
      e.preventDefault();
      onClose();
      navigate('/app/account', { state: { fromTemplates: true } });
      return;
    }
    
    // Track template usage
    if (template?.id) {
      useTemplate(template.id).catch(err => {
        console.error('Failed to record template usage:', err);
      });
    }
    
    // For premium/enterprise users, open Canva link if available
    if (template.canvaLink) {
      window.open(template.canvaLink, '_blank');
    } else {
      addToast('This template does not have an editable Canva version available.', 'info');
    }
  };

  // Handle tooltip show/hide with proper positioning
  const handleTooltipShow = () => {
    if (tooltipTriggerRef.current) {
      const rect = tooltipTriggerRef.current.getBoundingClientRect();
      const tooltipWidth = Math.min(320, window.innerWidth - 32);
      const tooltipHeight = 200;
      const padding = 12;
      
      // Check if there's enough space below
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < tooltipHeight + padding;
      
      // Calculate horizontal position - center on trigger, but keep within viewport
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      
      // Keep within viewport bounds
      if (left < padding) {
        left = padding;
      } else if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }
      
      setTooltipPosition({
        top: showAbove ? rect.top - tooltipHeight - padding : rect.bottom + padding,
        left: left,
        width: tooltipWidth,
        showAbove: showAbove
      });
    }
    setShowTooltip(true);
  };

  const handleTooltipHide = () => {
    setShowTooltip(false);
  };

  if (!isOpen || !template) return null;

  const disclaimerText = "Templates based on publicly available designs used in real political campaigns and advertisements. The inclusion of these designs does not imply any affiliation, partnership, or connection with the original campaign, candidate, or organization. It does not constitute an endorsement of our services by them, nor does it grant any right or license to use their original branding, images, or other copyrighted materials, which remain the property of their respective owners.";

  // Tooltip rendered via portal to ensure it's above everything
  const tooltipPortal = showTooltip && createPortal(
    <div 
      className="fixed bg-gray-900 text-white rounded-xl shadow-2xl transition-opacity duration-150 pointer-events-none"
      style={{ 
        top: `${tooltipPosition.top}px`, 
        left: `${tooltipPosition.left}px`,
        width: `${tooltipPosition.width}px`,
        padding: '14px 18px',
        fontSize: '0.8125rem',
        lineHeight: '1.5',
        zIndex: 99999,
        opacity: showTooltip ? 1 : 0,
      }}
    >
      {/* Arrow */}
      <div 
        className={`absolute ${tooltipPosition.showAbove ? 'top-full' : 'bottom-full'} left-1/2 -translate-x-1/2`}
        style={{ 
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          ...(tooltipPosition.showAbove 
            ? { borderTop: '10px solid #111827' }
            : { borderBottom: '10px solid #111827' }
          )
        }}
      />
      {disclaimerText}
    </div>,
    document.body
  );

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/60 backdrop-blur-sm">
        <div 
          className="animate-in fade-in zoom-in-95 duration-200 ease-out"
          style={{ animationFillMode: 'forwards' }}
        >
          <div 
            ref={modalRef}
            className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden"
          >
            {/* Close button - positioned in corner */}
            <button 
              onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>

            {/* Save button - positioned in corner */}
            {isLoggedIn && onSaveTemplate && (
              <button
                onClick={handleSaveClick}
                className={`absolute top-3 left-3 p-2 rounded-full z-20 shadow-md ${
                  isSaved 
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600' 
                    : 'bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700'
                } transition-colors`}
                aria-label={isSaved ? "Unsave template" : "Save template"}
              >
                <Star 
                  className={`w-4 h-4 ${isSaved ? 'text-white fill-white' : 'text-gray-600 dark:text-gray-300'}`} 
                />
              </button>
            )}

            {/* Main Content */}
            <div className="p-6 pt-14 md:p-8 md:pt-14">
              {/* Inline-flex container ensures button matches image width */}
              <div className="inline-flex flex-col">
                {/* Template image */}
                {template.type?.toLowerCase() === 'video' && template.videoUrl ? (
                  <video 
                    ref={videoRef}
                    src={template.videoUrl.startsWith('gs://') 
                      ? `https://storage.googleapis.com/${template.videoUrl.substring(5)}` 
                      : template.videoUrl}
                    controls
                    autoPlay
                    muted
                    loop
                    className="max-w-full h-auto max-h-[58vh] md:max-h-[62vh] object-contain rounded-lg shadow-md"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img 
                    src={template.previewImageURL} 
                    alt="Template preview"
                    className="max-w-full h-auto max-h-[58vh] md:max-h-[62vh] object-contain rounded-lg shadow-md"
                  />
                )}

                {/* Disclaimer tooltip trigger */}
                <div className="flex items-center justify-center mt-5 mb-4">
                  <div
                    ref={tooltipTriggerRef}
                    className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-help transition-colors"
                    onMouseEnter={handleTooltipShow}
                    onMouseLeave={handleTooltipHide}
                    onFocus={handleTooltipShow}
                    onBlur={handleTooltipHide}
                    tabIndex={0}
                  >
                    <Info className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Designs based on public campaigns. No affiliation or endorsement implied.</span>
                  </div>
                </div>

                {/* Edit in Canva button - matches image width */}
                <button
                  ref={canvaButtonRef}
                  onClick={handleCanvaClick}
                  className="w-full py-3.5 px-6 rounded-lg text-base font-semibold text-white transition-all flex items-center justify-center shadow-md hover:shadow-lg"
                  style={{
                    background: "linear-gradient(45deg, #00C4CC, #7D2AE8)",
                  }}
                >
                  Edit in Canva
                </button>

                {/* Close button - only visible on mobile */}
                <button
                  onClick={onClose}
                  className="w-full mt-3 py-2.5 px-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors lg:hidden"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tooltip rendered via portal */}
      {tooltipPortal}
    </>
  );
};

export default TemplateModal;
